import { API_BASE_URL } from './api';


const RECONNECT_DELAYS_MS = [1000, 2000, 5000, 10000, 30000];
const AUTH_CLOSE_CODES = new Set([4001, 4003, 4401, 4403]);


const buildSocketBaseUrl = () => {
  const parsedUrl = new URL(API_BASE_URL);
  const protocol = parsedUrl.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${parsedUrl.host}`;
};


class AdminNotificationSocket {
  constructor() {
    this.socket = null;
    this.listeners = new Set();
    this.connectionState = 'idle';
    this.reconnectTimer = null;
    this.pingTimer = null;
    this.reconnectAttempt = 0;
    this.manualClose = false;
  }

  getToken() {
    return localStorage.getItem('token');
  }

  buildUrl() {
    const token = this.getToken();
    const query = token ? `?token=${encodeURIComponent(token)}` : '';
    return `${buildSocketBaseUrl()}/ws/admin/notifications/${query}`;
  }

  subscribe(callback) {
    this.listeners.add(callback);
    callback({
      kind: 'status',
      status: this.connectionState,
    });

    return () => {
      this.listeners.delete(callback);
    };
  }

  emit(event) {
    this.listeners.forEach((listener) => {
      try {
        listener(event);
      } catch (error) {
        console.error('Admin notification socket listener error', error);
      }
    });
  }

  setConnectionState(status, extra = {}) {
    this.connectionState = status;
    this.emit({
      kind: 'status',
      status,
      ...extra,
    });
  }

  clearReconnectTimer() {
    if (this.reconnectTimer) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  clearPingTimer() {
    if (this.pingTimer) {
      window.clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  startPing() {
    this.clearPingTimer();
    this.pingTimer = window.setInterval(() => {
      if (this.socket?.readyState === WebSocket.OPEN) {
        this.socket.send(JSON.stringify({ type: 'ping', timestamp: Date.now() }));
      }
    }, 30000);
  }

  scheduleReconnect(reason) {
    if (this.reconnectTimer || this.manualClose || !this.getToken()) {
      return;
    }

    const delay = RECONNECT_DELAYS_MS[
      Math.min(this.reconnectAttempt, RECONNECT_DELAYS_MS.length - 1)
    ];
    this.reconnectAttempt += 1;
    this.setConnectionState('reconnecting', { reason, reconnectInMs: delay });

    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  connect() {
    const token = this.getToken();
    if (!token) {
      this.setConnectionState('idle', { reason: 'missing_token' });
      return;
    }

    if (
      this.socket
      && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    this.manualClose = false;
    this.clearReconnectTimer();
    this.setConnectionState('connecting');

    try {
      const socket = new WebSocket(this.buildUrl());
      this.socket = socket;

      socket.onopen = () => {
        this.reconnectAttempt = 0;
        this.setConnectionState('connected');
        this.startPing();
      };

      socket.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
          this.emit({
            kind: 'message',
            payload,
          });
        } catch (error) {
          this.emit({
            kind: 'error',
            error,
            message: 'Failed to parse admin notification websocket payload.',
          });
        }
      };

      socket.onerror = () => {
        this.emit({
          kind: 'error',
          message: 'Admin notification websocket encountered an error.',
        });
      };

      socket.onclose = (event) => {
        this.clearPingTimer();
        this.socket = null;

        if (this.manualClose) {
          this.setConnectionState('idle', { closeCode: event.code });
          return;
        }

        if (AUTH_CLOSE_CODES.has(event.code)) {
          this.setConnectionState('auth_error', { closeCode: event.code });
          return;
        }

        this.setConnectionState('disconnected', { closeCode: event.code });
        this.scheduleReconnect(`close_code_${event.code}`);
      };
    } catch (error) {
      this.setConnectionState('disconnected', { reason: 'socket_construction_failed' });
      this.emit({
        kind: 'error',
        error,
        message: 'Failed to create admin notification websocket connection.',
      });
      this.scheduleReconnect('socket_construction_failed');
    }
  }

  disconnect({ manual = true } = {}) {
    this.manualClose = manual;
    this.clearReconnectTimer();
    this.clearPingTimer();

    if (this.socket) {
      try {
        this.socket.close();
      } catch (error) {
        console.error('Failed to close admin notification websocket', error);
      }
      this.socket = null;
    }

    this.setConnectionState('idle', { reason: manual ? 'manual_disconnect' : 'disconnect' });
  }
}


const adminNotificationSocket = new AdminNotificationSocket();

export default adminNotificationSocket;
